import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, IconButton, Chip, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, Select, MenuItem, FormControl,
  InputLabel, Switch, FormControlLabel, Alert, Snackbar, Divider, Stack,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Newspaper as NewspaperIcon,
  QuizOutlined as QuizIcon,
  CloudUpload as UploadIcon,
  Close as CloseIcon,
  Videocam as VideoIcon,
} from '@mui/icons-material';
import api, { govtApi } from '../services/api';

interface CurrentAffairs {
  id: string;
  title: string;
  summary: string;
  content?: string;
  image_url?: string;
  video_url?: string;
  category?: string;
  source?: string;
  published_date: string;
  is_active: boolean;
  question_count: number;
}

interface CAQuestion {
  id: string;
  question: string;
  option_a: string; option_b: string; option_c: string; option_d: string;
  answer: string; explanation?: string;
}

const EMPTY_FORM = {
  title: '', summary: '', content: '', image_url: '', video_url: '',
  category: '', source: '', published_date: new Date().toISOString().slice(0, 10),
  is_active: true,
};

// image_url/video_url hold either a plain external URL (pasted) or a private
// S3 object key (uploaded) — same convention as question images elsewhere in
// this admin panel. Keys need a signed URL to preview.
const isS3Key = (v?: string) => !!v && !/^https?:\/\//i.test(v);

const EMPTY_QUESTION = {
  question: '', option_a: '', option_b: '', option_c: '', option_d: '',
  answer: 'A', explanation: '',
};

const CurrentAffairsPage: React.FC = () => {
  const [items, setItems] = useState<CurrentAffairs[]>([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as any });

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<CurrentAffairs | null>(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [imagePreview, setImagePreview] = useState('');
  const [videoPreview, setVideoPreview] = useState('');
  const [imageUploading, setImageUploading] = useState(false);
  const [videoUploading, setVideoUploading] = useState(false);

  const [questionsDialogOpen, setQuestionsDialogOpen] = useState(false);
  const [activeArticle, setActiveArticle] = useState<CurrentAffairs | null>(null);
  const [questions, setQuestions] = useState<CAQuestion[]>([]);
  const [qForm, setQForm] = useState(EMPTY_QUESTION);

  const showSnackbar = (message: string, severity: 'success' | 'error') => setSnackbar({ open: true, message, severity });

  const load = useCallback(async () => {
    try {
      const res = await govtApi.get('/govt/admin/current-affairs', { params: { pageSize: 100 } });
      setItems(res.data.data || []);
    } catch {
      showSnackbar('Failed to load current affairs', 'error');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setImagePreview('');
    setVideoPreview('');
    setDialogOpen(true);
  };

  const openEdit = async (item: CurrentAffairs) => {
    setEditing(item);
    setForm({
      title: item.title, summary: item.summary, content: item.content || '',
      image_url: item.image_url || '', video_url: item.video_url || '',
      category: item.category || '', source: item.source || '',
      published_date: item.published_date?.slice(0, 10), is_active: item.is_active,
    });
    setDialogOpen(true);
    // adminList() preserves raw S3 keys (not directly browsable) — resolve to a
    // fresh signed URL for previewing, same as the question-image picker.
    setImagePreview('');
    setVideoPreview('');
    if (item.image_url && isS3Key(item.image_url)) {
      try {
        const r = await api.get('/admin/upload/resolve', { params: { key: item.image_url } });
        setImagePreview(r.data.data.url);
      } catch { /* ignore, picker will just show empty preview */ }
    } else if (item.image_url) {
      setImagePreview(item.image_url);
    }
    if (item.video_url && isS3Key(item.video_url)) {
      try {
        const r = await api.get('/admin/upload/resolve', { params: { key: item.video_url } });
        setVideoPreview(r.data.data.url);
      } catch { /* ignore */ }
    } else if (item.video_url) {
      setVideoPreview(item.video_url);
    }
  };

  const uploadImage = async (file: File) => {
    setImageUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const r = await api.post('/admin/upload/image?folder=current-affairs', formData, {
        headers: { 'Content-Type': undefined },
      });
      const { key, url } = r.data.data;
      setForm((f) => ({ ...f, image_url: key }));
      setImagePreview(url);
    } catch {
      showSnackbar('Image upload failed', 'error');
    } finally {
      setImageUploading(false);
    }
  };

  const uploadVideo = async (file: File) => {
    setVideoUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const r = await api.post('/admin/upload/video?folder=current-affairs', formData, {
        headers: { 'Content-Type': undefined },
      });
      const { key, url } = r.data.data;
      setForm((f) => ({ ...f, video_url: key }));
      setVideoPreview(url);
    } catch {
      showSnackbar('Video upload failed', 'error');
    } finally {
      setVideoUploading(false);
    }
  };

  const save = async () => {
    if (!form.title || !form.summary || !form.published_date) {
      showSnackbar('Title, summary and date are required', 'error'); return;
    }
    try {
      if (editing) {
        await govtApi.put(`/govt/admin/current-affairs/${editing.id}`, form);
        showSnackbar('Article updated', 'success');
      } else {
        await govtApi.post('/govt/admin/current-affairs', form);
        showSnackbar('Article created', 'success');
      }
      setDialogOpen(false);
      load();
    } catch {
      showSnackbar('Failed to save article', 'error');
    }
  };

  const remove = async (id: string) => {
    if (!window.confirm('Delete this article and its linked questions?')) return;
    try {
      await govtApi.delete(`/govt/admin/current-affairs/${id}`);
      showSnackbar('Article deleted', 'success');
      load();
    } catch {
      showSnackbar('Failed to delete article', 'error');
    }
  };

  const openQuestions = async (item: CurrentAffairs) => {
    setActiveArticle(item);
    setQForm(EMPTY_QUESTION);
    setQuestionsDialogOpen(true);
    try {
      const res = await govtApi.get(`/govt/admin/current-affairs/${item.id}/questions`);
      setQuestions(res.data || []);
    } catch {
      showSnackbar('Failed to load questions', 'error');
    }
  };

  const addQuestion = async () => {
    if (!activeArticle) return;
    const { question, option_a, option_b, option_c, option_d, answer } = qForm;
    if (!question || !option_a || !option_b || !option_c || !option_d || !answer) {
      showSnackbar('All question fields are required', 'error'); return;
    }
    try {
      await govtApi.post(`/govt/admin/current-affairs/${activeArticle.id}/questions/new`, qForm);
      showSnackbar('Question added', 'success');
      setQForm(EMPTY_QUESTION);
      const res = await govtApi.get(`/govt/admin/current-affairs/${activeArticle.id}/questions`);
      setQuestions(res.data || []);
      load();
    } catch {
      showSnackbar('Failed to add question', 'error');
    }
  };

  const removeQuestion = async (questionId: string) => {
    if (!activeArticle) return;
    try {
      await govtApi.delete(`/govt/admin/current-affairs/${activeArticle.id}/questions/${questionId}`);
      setQuestions((q) => q.filter((x) => x.id !== questionId));
      load();
    } catch {
      showSnackbar('Failed to remove question', 'error');
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <NewspaperIcon sx={{ fontSize: 40, color: 'primary.main' }} />
          <Box>
            <Typography variant="h4" fontWeight="bold">Current Affairs</Typography>
            <Typography variant="body2" color="text.secondary">
              Daily digest articles shown in the app's swipe/list feed, with optional quiz questions
            </Typography>
          </Box>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate} size="large">
          New Article
        </Button>
      </Box>

      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Title</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Questions</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>{new Date(item.published_date).toLocaleDateString()}</TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">{item.title}</Typography>
                    <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 400, display: 'block' }}>
                      {item.summary}
                    </Typography>
                  </TableCell>
                  <TableCell>{item.category ? <Chip label={item.category} size="small" /> : '—'}</TableCell>
                  <TableCell>
                    <Button size="small" startIcon={<QuizIcon />} onClick={() => openQuestions(item)}>
                      {item.question_count} question{item.question_count === 1 ? '' : 's'}
                    </Button>
                  </TableCell>
                  <TableCell>
                    <Chip label={item.is_active ? 'Active' : 'Inactive'} size="small" color={item.is_active ? 'success' : 'default'} />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => openEdit(item)}><EditIcon /></IconButton>
                    <IconButton size="small" color="error" onClick={() => remove(item.id)}><DeleteIcon /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {items.length === 0 && (
                <TableRow><TableCell colSpan={6} align="center">No articles yet</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Create/Edit Article */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editing ? 'Edit Article' : 'New Article'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Title" fullWidth required value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })} />
            <TextField label="Summary (shown on card / list)" fullWidth required multiline rows={2}
              value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} />
            <TextField label="Full content (detail page)" fullWidth multiline rows={6}
              value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })}
              helperText="Leave blank to only show the summary; 'Read More' only appears on the detail page if this is filled in" />
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>Image (optional)</Typography>
              <Stack direction="row" spacing={2} alignItems="flex-start">
                <Box sx={{ position: 'relative', width: 96, height: 96, flexShrink: 0 }}>
                  {imagePreview ? (
                    <>
                      <img src={imagePreview} alt="preview" style={{ width: 96, height: 96, objectFit: 'cover', borderRadius: 8, border: '1px solid #E5E7EB' }} />
                      <IconButton
                        size="small"
                        onClick={() => { setForm({ ...form, image_url: '' }); setImagePreview(''); }}
                        sx={{ position: 'absolute', top: -8, right: -8, bgcolor: 'background.paper', boxShadow: 1, p: 0.3 }}>
                        <CloseIcon fontSize="small" color="error" />
                      </IconButton>
                    </>
                  ) : (
                    <Button component="label" variant="outlined" disabled={imageUploading}
                      sx={{ width: 96, height: 96, display: 'flex', flexDirection: 'column', gap: 0.5, borderStyle: 'dashed' }}>
                      <UploadIcon fontSize="small" />
                      <Typography variant="caption">{imageUploading ? 'Uploading…' : 'Upload'}</Typography>
                      <input hidden type="file" accept="image/jpeg,image/png,image/webp,image/gif"
                        onChange={(e) => e.target.files?.[0] && uploadImage(e.target.files[0])} />
                    </Button>
                  )}
                </Box>
                <TextField label="or paste an image URL" fullWidth size="small" value={isS3Key(form.image_url) ? '' : form.image_url}
                  placeholder={isS3Key(form.image_url) ? '(file uploaded)' : 'https://...'}
                  disabled={isS3Key(form.image_url)}
                  onChange={(e) => { setForm({ ...form, image_url: e.target.value }); setImagePreview(e.target.value); }} />
              </Stack>
            </Box>

            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>Video (optional)</Typography>
              <Stack direction="row" spacing={2} alignItems="flex-start">
                <Box sx={{ position: 'relative', width: 96, height: 96, flexShrink: 0 }}>
                  {videoPreview ? (
                    <>
                      <Box sx={{ width: 96, height: 96, borderRadius: 1, border: '1px solid #E5E7EB', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#F9FAFB' }}>
                        <VideoIcon color="primary" />
                      </Box>
                      <IconButton
                        size="small"
                        onClick={() => { setForm({ ...form, video_url: '' }); setVideoPreview(''); }}
                        sx={{ position: 'absolute', top: -8, right: -8, bgcolor: 'background.paper', boxShadow: 1, p: 0.3 }}>
                        <CloseIcon fontSize="small" color="error" />
                      </IconButton>
                    </>
                  ) : (
                    <Button component="label" variant="outlined" disabled={videoUploading}
                      sx={{ width: 96, height: 96, display: 'flex', flexDirection: 'column', gap: 0.5, borderStyle: 'dashed' }}>
                      <UploadIcon fontSize="small" />
                      <Typography variant="caption">{videoUploading ? 'Uploading…' : 'Upload'}</Typography>
                      <input hidden type="file" accept="video/mp4,video/quicktime,video/webm"
                        onChange={(e) => e.target.files?.[0] && uploadVideo(e.target.files[0])} />
                    </Button>
                  )}
                </Box>
                <TextField label="or paste a video URL (e.g. YouTube link)" fullWidth size="small" value={isS3Key(form.video_url) ? '' : form.video_url}
                  placeholder={isS3Key(form.video_url) ? '(file uploaded)' : 'https://...'}
                  disabled={isS3Key(form.video_url)}
                  onChange={(e) => { setForm({ ...form, video_url: e.target.value }); setVideoPreview(e.target.value); }} />
              </Stack>
            </Box>

            <Stack direction="row" spacing={2}>
              <TextField label="Category (optional)" fullWidth value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="National, Karnataka, Sports..." />
              <TextField label="Source (optional)" fullWidth value={form.source}
                onChange={(e) => setForm({ ...form, source: e.target.value })} />
            </Stack>
            <TextField label="Published Date" type="date" fullWidth required
              value={form.published_date} onChange={(e) => setForm({ ...form, published_date: e.target.value })}
              InputLabelProps={{ shrink: true }} />
            <FormControlLabel
              control={<Switch checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />}
              label="Active" />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={save}>{editing ? 'Update' : 'Create'}</Button>
        </DialogActions>
      </Dialog>

      {/* Manage Questions */}
      <Dialog open={questionsDialogOpen} onClose={() => setQuestionsDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Questions — {activeArticle?.title}</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ mb: 3 }}>
            {questions.map((q, i) => (
              <Paper key={q.id} variant="outlined" sx={{ p: 1.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Typography variant="body2" fontWeight="bold" sx={{ flex: 1 }}>{i + 1}. {q.question}</Typography>
                  <IconButton size="small" color="error" onClick={() => removeQuestion(q.id)}><DeleteIcon fontSize="small" /></IconButton>
                </Box>
                <Typography variant="caption" color="text.secondary">
                  A) {q.option_a}  B) {q.option_b}  C) {q.option_c}  D) {q.option_d} — Answer: {q.answer}
                </Typography>
              </Paper>
            ))}
            {questions.length === 0 && (
              <Typography variant="body2" color="text.secondary">No questions linked yet.</Typography>
            )}
          </Stack>

          <Divider sx={{ mb: 2 }} />
          <Typography variant="subtitle2" sx={{ mb: 1.5 }}>Add a question</Typography>
          <Stack spacing={2}>
            <TextField label="Question" fullWidth multiline rows={2} value={qForm.question}
              onChange={(e) => setQForm({ ...qForm, question: e.target.value })} />
            <Stack direction="row" spacing={2}>
              <TextField label="Option A" fullWidth value={qForm.option_a} onChange={(e) => setQForm({ ...qForm, option_a: e.target.value })} />
              <TextField label="Option B" fullWidth value={qForm.option_b} onChange={(e) => setQForm({ ...qForm, option_b: e.target.value })} />
            </Stack>
            <Stack direction="row" spacing={2}>
              <TextField label="Option C" fullWidth value={qForm.option_c} onChange={(e) => setQForm({ ...qForm, option_c: e.target.value })} />
              <TextField label="Option D" fullWidth value={qForm.option_d} onChange={(e) => setQForm({ ...qForm, option_d: e.target.value })} />
            </Stack>
            <Stack direction="row" spacing={2}>
              <FormControl sx={{ minWidth: 160 }}>
                <InputLabel>Correct Answer</InputLabel>
                <Select value={qForm.answer} label="Correct Answer" onChange={(e) => setQForm({ ...qForm, answer: e.target.value })}>
                  {['A', 'B', 'C', 'D'].map((v) => <MenuItem key={v} value={v}>Option {v}</MenuItem>)}
                </Select>
              </FormControl>
              <TextField label="Explanation (optional)" fullWidth value={qForm.explanation}
                onChange={(e) => setQForm({ ...qForm, explanation: e.target.value })} />
            </Stack>
            <Button variant="outlined" startIcon={<AddIcon />} onClick={addQuestion} sx={{ alignSelf: 'flex-start' }}>
              Add Question
            </Button>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setQuestionsDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CurrentAffairsPage;
